import { useState } from 'react';
import { DentalChart, ToothData } from '../../types';
import { AlertTriangle, CheckCircle, X } from 'lucide-react';

export default function ToothChart({
  dentalChart,
  viewMode
}: {
  dentalChart: DentalChart;
  viewMode: '2d' | '3d';
}) {
  const [selectedTooth, setSelectedTooth] = useState<ToothData | null>(null);

  const getToothColor = (condition: ToothData['condition']) => {
    switch (condition) {
      case 'healthy':
        return 'bg-green-100 border-green-400 text-green-800';
      case 'cavity':
        return 'bg-red-100 border-red-400 text-red-800';
      case 'filling':
        return 'bg-blue-100 border-blue-400 text-blue-800';
      case 'crown':
        return 'bg-yellow-100 border-yellow-400 text-yellow-800';
      case 'missing':
        return 'bg-gray-100 border-gray-400 text-gray-800';
      case 'implant':
        return 'bg-purple-100 border-purple-400 text-purple-800';
      default:
        return 'bg-gray-100 border-gray-400 text-gray-800';
    }
  };

  const getConditionIcon = (condition: ToothData['condition']) => {
    switch (condition) {
      case 'healthy':
        return <CheckCircle className="w-4 h-4" />;
      case 'cavity':
      case 'missing':
        return <AlertTriangle className="w-4 h-4" />;
      case 'filling':
      case 'crown':
      case 'implant':
        return <CheckCircle className="w-4 h-4" />;
      default:
        return null;
    }
  };

  // Universal Tooth Numbering System
  const upperTeeth = dentalChart.teeth.filter(t => parseInt(t.number) <= 16).sort((a, b) => parseInt(a.number) - parseInt(b.number));
  const lowerTeeth = dentalChart.teeth.filter(t => parseInt(t.number) > 16).sort((a, b) => parseInt(a.number) - parseInt(b.number));

  if (viewMode === '3d') {
    return (
      <div className="bg-gradient-to-b from-gray-100 to-gray-200 rounded-lg p-8">
        <div className="text-center py-12">
          <div className="w-24 h-24 mx-auto mb-4 bg-white rounded-full flex items-center justify-center shadow-lg">
            <svg className="w-16 h-16 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-700 mb-2">3D Dental Chart</h3>
          <p className="text-gray-600">Interactive 3D visualization coming soon</p>
          <p className="text-sm text-gray-500 mt-2">Will feature rotatable 3D models, AR visualization, and advanced diagnostics</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 2D Chart */}
      <div className="bg-gray-50 rounded-lg p-6 border border-gray-200">
        {/* Upper Jaw */}
        <div className="mb-8">
          <div className="text-center mb-3">
            <span className="text-xs font-semibold text-gray-600 uppercase">Upper Jaw</span>
          </div>
          <div className="grid grid-cols-8 gap-2 lg:gap-3">
            {upperTeeth.slice(0, 8).reverse().map((tooth) => (
              <ToothButton
                key={tooth.number}
                tooth={tooth}
                getToothColor={getToothColor}
                onClick={() => setSelectedTooth(tooth)}
                isSelected={selectedTooth?.number === tooth.number}
              />
            ))}
          </div>
          <div className="h-px bg-gray-300 my-4"></div>
          <div className="grid grid-cols-8 gap-2 lg:gap-3">
            {upperTeeth.slice(8).map((tooth) => (
              <ToothButton
                key={tooth.number}
                tooth={tooth}
                getToothColor={getToothColor}
                onClick={() => setSelectedTooth(tooth)}
                isSelected={selectedTooth?.number === tooth.number}
              />
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t-2 border-gray-400"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-4 bg-gray-50 text-gray-600 font-medium">BITE LINE</span>
          </div>
        </div>

        {/* Lower Jaw */}
        <div>
          <div className="grid grid-cols-8 gap-2 lg:gap-3 mb-4">
            {lowerTeeth.slice(0, 8).reverse().map((tooth) => (
              <ToothButton
                key={tooth.number}
                tooth={tooth}
                getToothColor={getToothColor}
                onClick={() => setSelectedTooth(tooth)}
                isSelected={selectedTooth?.number === tooth.number}
              />
            ))}
          </div>
          <div className="h-px bg-gray-300 my-4"></div>
          <div className="grid grid-cols-8 gap-2 lg:gap-3">
            {lowerTeeth.slice(8).map((tooth) => (
              <ToothButton
                key={tooth.number}
                tooth={tooth}
                getToothColor={getToothColor}
                onClick={() => setSelectedTooth(tooth)}
                isSelected={selectedTooth?.number === tooth.number}
              />
            ))}
          </div>
          <div className="text-center mt-3">
            <span className="text-xs font-semibold text-gray-600 uppercase">Lower Jaw</span>
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="bg-white rounded-lg p-4 border border-gray-200">
        <h4 className="font-semibold text-gray-900 mb-3">Legend</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <LegendItem condition="healthy" label="Healthy" />
          <LegendItem condition="cavity" label="Cavity" />
          <LegendItem condition="filling" label="Filling" />
          <LegendItem condition="crown" label="Crown" />
          <LegendItem condition="missing" label="Missing" />
          <LegendItem condition="implant" label="Implant" />
        </div>
      </div>

      {/* Selected Tooth Details */}
      {selectedTooth && (
        <div className="bg-white rounded-lg p-4 border-2 border-green-500">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h4 className="font-bold text-gray-900 text-lg">Tooth #{selectedTooth.number}</h4>
              <span className={`inline-flex items-center space-x-1 px-3 py-1 rounded-full text-sm font-semibold ${getToothColor(selectedTooth.condition)}`}>
                {getConditionIcon(selectedTooth.condition)}
                <span className="capitalize">{selectedTooth.condition}</span>
              </span>
            </div>
            <button
              onClick={() => setSelectedTooth(null)}
              className="p-2 text-gray-400 hover:text-gray-600 rounded-lg"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          {selectedTooth.notes && (
            <div className="bg-gray-50 rounded p-3 mb-2">
              <p className="text-sm text-gray-700">{selectedTooth.notes}</p>
            </div>
          )}
          <p className="text-xs text-gray-500">Last examined: {selectedTooth.lastExamined}</p>
        </div>
      )}

      {/* Chart Info */}
      <div className="bg-blue-50 rounded-lg p-4 border border-blue-200">
        <div className="flex items-start space-x-3">
          <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <div>
            <p className="text-sm font-medium text-blue-900">Chart Information</p>
            <p className="text-xs text-blue-700 mt-1">
              Last Updated: {new Date(dentalChart.lastUpdated).toLocaleDateString()} •
              Universal Numbering System (1-32)
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ToothButton({
  tooth,
  getToothColor,
  onClick,
  isSelected
}: {
  tooth: ToothData;
  getToothColor: (condition: ToothData['condition']) => string;
  onClick: () => void;
  isSelected: boolean;
}) {
  return (
    <button
      onClick={onClick}
      className={`relative aspect-square border-2 rounded-lg transition-all hover:scale-110 hover:shadow-lg ${
        getToothColor(tooth.condition)
      } ${isSelected ? 'ring-4 ring-green-500 scale-110 shadow-lg' : ''}`}
      title={`Tooth #${tooth.number} - ${tooth.condition}`}
    >
      <div className="flex flex-col items-center justify-center h-full">
        <span className="text-xs font-bold">{tooth.number}</span>
        {tooth.condition !== 'healthy' && tooth.condition !== 'missing' && (
          <div className="absolute bottom-1 right-1 w-2 h-2 bg-current rounded-full"></div>
        )}
      </div>
    </button>
  );
}

function LegendItem({ condition, label }: { condition: ToothData['condition']; label: string }) {
  const getToothColor = (condition: ToothData['condition']) => {
    switch (condition) {
      case 'healthy':
        return 'bg-green-100 border-green-400 text-green-800';
      case 'cavity':
        return 'bg-red-100 border-red-400 text-red-800';
      case 'filling':
        return 'bg-blue-100 border-blue-400 text-blue-800';
      case 'crown':
        return 'bg-yellow-100 border-yellow-400 text-yellow-800';
      case 'missing':
        return 'bg-gray-100 border-gray-400 text-gray-800';
      case 'implant':
        return 'bg-purple-100 border-purple-400 text-purple-800';
      default:
        return 'bg-gray-100 border-gray-400 text-gray-800';
    }
  };

  return (
    <div className="flex items-center space-x-2">
      <div className={`w-6 h-6 border-2 rounded ${getToothColor(condition)}`}></div>
      <span className="text-sm text-gray-700">{label}</span>
    </div>
  );
}
